import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Mail, Phone, ShieldCheck, X, AlertTriangle, Users2, Trash2, Edit2, Ruler, Copy, Link as LinkIcon } from "lucide-react";
import { fmtNL } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["OWNER", "PLANNER", "UITVOERDER"];

export default function Medewerkers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [certs, setCerts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [seatUsage, setSeatUsage] = useState(null);
  const [plannedMap, setPlannedMap] = useState({});
  const [showModal, setShowModal] = useState(null); // null | "new" | user object
  const [showTeams, setShowTeams] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const office = ["OWNER", "PLANNER"].includes(user?.role);
  const isOwner = user?.role === "OWNER";

  const load = async () => {
    const [u, c, t, s, pu] = await Promise.all([
      api.get("/users"),
      api.get("/certificates"),
      api.get("/teams"),
      api.get("/billing/seat-usage").catch(() => ({ data: null })),
      api.get("/billing/planned-users-this-month").catch(() => ({ data: null })),
    ]);
    setUsers(u.data);
    setCerts(c.data);
    setTeams(t.data);
    setSeatUsage(s.data);
    const map = {};
    (pu.data?.users || []).forEach((x) => { map[x.user_id] = x; });
    setPlannedMap(map);
  };
  useEffect(() => { load(); }, []);

  const copyInvite = async (uid) => {
    try {
      const r = await api.get(`/users/${uid}/invite-link`);
      const { email, temp_password, login_url } = r.data;
      const block = `Ploegbord inloggegevens\nE-mail: ${email}\nWachtwoord: ${temp_password}\nInloggen: ${login_url}`;
      await navigator.clipboard.writeText(block);
      toast.success("Inloggegevens gekopieerd + nieuwe uitnodigingsmail verstuurd.", { duration: 10000 });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kon inloggegevens niet ophalen — probeer opnieuw of vernieuw de pagina.";
      toast.error(typeof msg === "string" ? msg : "Kon inloggegevens niet ophalen");
    }
  };

  const buySeat = async () => {
    if (!confirm("1 extra seat kopen? Stripe verrekent direct proration.")) return;
    try {
      await api.post("/billing/buy-seat");
      toast.success("Extra seat toegevoegd");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aankoop mislukt"); }
  };

  const expiring = certs.filter((c) => {
    if (!c.valid_until) return false;
    const diff = (new Date(c.valid_until) - new Date()) / (1000*60*60*24);
    return diff >= 0 && diff <= 60;
  });

  const setUserTeam = async (uid, teamId) => {
    try {
      await api.patch(`/users/${uid}`, { team_id: teamId ? teamId : null });
      toast.success("Team bijgewerkt");
      await load();
    } catch { toast.error("Kon niet opslaan"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success("Medewerker verwijderd");
      setConfirmDelete(null);
      await load();
    } catch { toast.error("Kon niet verwijderen"); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="pb-heading text-3xl font-black">MEDEWERKERS</h1>
        <div className="flex gap-2">
          {office && (
            <>
              <button className="pb-btn-outline flex items-center gap-2" onClick={() => setShowTeams(true)} data-testid="btn-manage-teams">
                <Users2 size={18}/> Teams beheren
              </button>
              <button className="pb-btn-primary flex items-center gap-2" onClick={() => setShowModal("new")} data-testid="btn-new-user">
                <Plus size={18}/> Nieuwe medewerker
              </button>
            </>
          )}
        </div>
      </div>

      {seatUsage && (
        <div className="pb-card p-4 mb-4 grid md:grid-cols-2 gap-4" data-testid="seat-usage">
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500">Ingeplande gebruikers deze maand</div>
            <div className="pb-heading font-black text-lg mt-1 tabular-nums" data-testid="seat-usage-numbers">
              {seatUsage.planned_this_month ?? 0} / {seatUsage.included || 0}
              <span className="text-slate-500 text-sm font-bold"> inbegrepen</span>
            </div>
            <div className="text-xs font-semibold text-slate-500">
              {seatUsage.tier ? (
                <>Boven {seatUsage.included} betaal je per extra uitvoerder. Kantoor is gratis.</>
              ) : (
                <>Je pakket wordt geactiveerd na start proefperiode.</>
              )}
            </div>
            <div className="mt-2 h-2 bg-slate-200 rounded overflow-hidden">
              <div className="h-full bg-orange-600" style={{ width: `${Math.min(100, ((seatUsage.planned_this_month || 0) / Math.max(1, seatUsage.included || 1)) * 100)}%` }}/>
            </div>
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 flex items-center justify-between">
              <span>Kantooraccounts (Eigenaar + Planner)</span>
              {seatUsage.office_used >= seatUsage.office_limit && (
                <span className="bg-red-600 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-full">Limiet bereikt</span>
              )}
            </div>
            <div className="pb-heading font-black text-lg mt-1 tabular-nums" data-testid="office-usage-numbers">
              {seatUsage.office_used ?? 0} van {seatUsage.office_limit ?? 0} gebruikt
            </div>
            <div className="text-xs font-semibold text-slate-500">
              Kantoor is altijd gratis. Boven de grens: upgrade je pakket.
            </div>
            <div className="mt-2 h-2 bg-slate-200 rounded overflow-hidden">
              <div className={`h-full ${seatUsage.office_used >= seatUsage.office_limit ? "bg-red-600" : "bg-slate-900"}`} style={{ width: `${Math.min(100, ((seatUsage.office_used || 0) / Math.max(1, seatUsage.office_limit || 1)) * 100)}%` }}/>
            </div>
          </div>
        </div>
      )}

      {expiring.length > 0 && false && (
        <div className="pb-card !border-orange-600 p-4 mb-4">
          <div className="flex items-center gap-2 text-orange-700 font-black uppercase text-sm tracking-widest"><AlertTriangle size={18}/> Certificaten verlopen binnen 60 dagen</div>
          <ul className="mt-2 space-y-1 text-sm font-semibold">
            {expiring.map((c) => {
              const u = users.find((x) => x.id === c.user_id);
              return <li key={c.id}>{u?.name || "?"} — {c.type} verloopt {fmtNL(c.valid_until)}</li>;
            })}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {users.map((u) => {
          const uCerts = certs.filter((c) => c.user_id === u.id);
          const team = teams.find((t) => t.id === u.team_id);
          return (
            <div key={u.id} className="pb-card p-4" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="pb-heading text-lg font-black truncate">{u.name}</div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="text-xs uppercase font-bold tracking-widest text-orange-600">{u.role}</div>
                    {(() => {
                      const info = plannedMap[u.id];
                      if (!info) return null;
                      if (info.is_office) {
                        return <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-900 text-white" data-testid={`billing-badge-${u.id}`}>Kantoor · gratis</span>;
                      }
                      if (info.is_billable) {
                        return <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-orange-600 text-white" data-testid={`billing-badge-${u.id}`}>Meetelt · {info.planned_days_this_month}d</span>;
                      }
                      return <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border-2 border-slate-300 text-slate-500" data-testid={`billing-badge-${u.id}`}>Niet ingepland</span>;
                    })()}
                  </div>
                  <div className="text-sm font-semibold text-slate-700">{u.function}</div>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  {office && (
                    <div className="flex gap-1">
                      {u.role === "UITVOERDER" && (
                        <button onClick={() => copyInvite(u.id)} className="p-2 border-2 border-orange-600 text-orange-600 rounded hover:bg-orange-50" data-testid={`btn-invite-${u.id}`} aria-label="Uitnodigingslink"><LinkIcon size={14}/></button>
                      )}
                      <button onClick={() => setShowModal(u)} className="p-2 border-2 border-slate-900 rounded hover:bg-orange-50" data-testid={`btn-edit-user-${u.id}`} aria-label="Bewerken"><Edit2 size={14}/></button>
                      {isOwner && u.id !== user.id && (
                        <button onClick={() => setConfirmDelete(u)} className="p-2 border-2 border-red-600 text-red-600 rounded hover:bg-red-50" data-testid={`btn-delete-user-${u.id}`} aria-label="Verwijderen"><Trash2 size={14}/></button>
                      )}
                    </div>
                  )}
                  {office && (u.hourly_rate !== undefined || u.sqm_rate !== undefined) && (
                    <div className="text-right">
                      {u.hourly_rate !== undefined && u.hourly_rate > 0 && (
                        <div className="text-xs font-bold text-slate-700">€ {u.hourly_rate}/u</div>
                      )}
                      {u.sqm_rate !== undefined && u.sqm_rate > 0 && (
                        <div className="text-xs font-bold text-slate-700 flex items-center gap-1 justify-end"><Ruler size={12}/> € {u.sqm_rate}/m²</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
                <div className="flex items-center gap-2"><Mail size={14}/> {u.email}</div>
                {u.phone && <div className="flex items-center gap-2"><Phone size={14}/> {u.phone}</div>}
              </div>

              {office && (
                <div className="mt-3 border-t-2 border-slate-200 pt-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1"><Users2 size={12}/> Team</label>
                  <select
                    className="pb-input !min-h-[40px] mt-1"
                    value={u.team_id || ""}
                    onChange={(e) => setUserTeam(u.id, e.target.value)}
                    data-testid={`user-team-${u.id}`}
                  >
                    <option value="">Geen team</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              )}
              {!office && team && (
                <div className="mt-3 border-t-2 border-slate-200 pt-2 text-sm font-semibold flex items-center gap-1"><Users2 size={14}/> Team: {team.name}</div>
              )}

              {false && uCerts.length > 0 && (
                <div className="mt-3 border-t-2 border-slate-200 pt-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><ShieldCheck size={14}/> Certificaten</div>
                  {uCerts.map((c) => (
                    <div key={c.id} className="text-sm font-semibold flex items-center justify-between">
                      <span>{c.type}</span>
                      <span className="text-xs text-slate-500">Geldig t/m {fmtNL(c.valid_until)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showModal && (
        <UserModal
          key={showModal === "new" ? "new" : showModal.id}
          existing={showModal === "new" ? null : showModal}
          teams={teams}
          onClose={() => setShowModal(null)}
          onSaved={() => { setShowModal(null); load(); }}
        />
      )}
      {showTeams && <TeamsModal onClose={() => setShowTeams(false)} teams={teams} users={users} onChanged={load}/>}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60">
          <div className="pb-card w-full max-w-sm p-5" data-testid="confirm-delete-user">
            <div className="pb-heading text-lg font-black mb-2">Medewerker verwijderen?</div>
            <div className="text-sm text-slate-700 font-medium mb-4">{confirmDelete.name} kan daarna niet meer inloggen en verschijnt niet meer op het planbord.</div>
            <div className="flex gap-2">
              <button className="pb-btn-outline flex-1" onClick={() => setConfirmDelete(null)}>Annuleren</button>
              <button className="pb-btn-primary !bg-red-600 flex-1" onClick={doDelete} data-testid="confirm-delete-user-ok">Verwijderen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserModal({ existing, teams, onClose, onSaved }) {
  const editing = !!existing;
  const [form, setForm] = useState({
    name: existing?.name || "",
    email: existing?.email || "",
    phone: existing?.phone || "",
    role: existing?.role || "UITVOERDER",
    function: existing?.function || "",
    hourly_rate: existing?.hourly_rate ?? 0,
    sqm_rate: existing?.sqm_rate ?? 0,
    team_id: existing?.team_id || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const patch = {
          name: form.name, phone: form.phone, role: form.role, function: form.function,
          hourly_rate: Number(form.hourly_rate) || 0,
          sqm_rate: Number(form.sqm_rate) || 0,
          team_id: form.team_id ? form.team_id : null,
        };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${existing.id}`, patch);
        toast.success("Medewerker bijgewerkt");
      } else {
        const body = { ...form, hourly_rate: Number(form.hourly_rate) || 0, sqm_rate: Number(form.sqm_rate) || 0 };
        if (!body.team_id) delete body.team_id;
        if (!body.password) delete body.password;
        const r = await api.post("/users", body);
        const { user: newUser, temp_password, login_url } = r.data;
        const clip = `Ploegbord inloggegevens\nE-mail: ${newUser.email}\nWachtwoord: ${temp_password}\nInloggen: ${login_url}`;
        try { await navigator.clipboard.writeText(clip); } catch { /* ignore */ }
        toast.success(
          `Medewerker aangemaakt. E-mail is verstuurd naar ${newUser.email}. Inloggegevens zijn ook naar het klembord gekopieerd — plak in WhatsApp om direct te delen.`,
          { duration: 14000 }
        );
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon medewerker niet opslaan");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50">
      <form onSubmit={submit} className="pb-card w-full max-w-md max-h-[95vh] overflow-y-auto pb-scrollbar" data-testid="user-modal">
        <div className="bg-slate-900 text-white p-4 flex items-center justify-between sticky top-0">
          <div className="pb-heading text-lg font-black">{editing ? "Medewerker bewerken" : "Nieuwe medewerker"}</div>
          <button type="button" onClick={onClose} className="text-white"><X size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <input className="pb-input" placeholder="Naam" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="nu-name"/>
          <input className="pb-input" placeholder="E-mail" type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} required disabled={editing} data-testid="nu-email"/>
          <input className="pb-input" placeholder="Telefoon" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})}/>
          <input className="pb-input" placeholder="Functie" value={form.function} onChange={(e)=>setForm({...form, function: e.target.value})}/>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-700">Rol</label>
          <select className="pb-input" value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} data-testid="nu-role">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-700">Team</label>
          <select className="pb-input" value={form.team_id} onChange={(e)=>setForm({...form, team_id: e.target.value})} data-testid="nu-team">
            <option value="">Geen team</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-700">Uurloon €</label>
              <input className="pb-input" type="number" step="0.01" value={form.hourly_rate} onChange={(e)=>setForm({...form, hourly_rate: e.target.value})} data-testid="nu-hourly"/>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-700">m²-tarief € (ZZP)</label>
              <input className="pb-input" type="number" step="0.01" value={form.sqm_rate} onChange={(e)=>setForm({...form, sqm_rate: e.target.value})} data-testid="nu-sqm"/>
            </div>
          </div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-700">{editing ? "Nieuw wachtwoord (leeg = niet wijzigen)" : "Wachtwoord (leeg = automatisch)"}</label>
          <input className="pb-input" type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} data-testid="nu-password" autoComplete="new-password"/>
        </div>
        <div className="p-4 border-t-2 border-slate-900 flex gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="pb-btn-outline flex-1">Annuleren</button>
          <button type="submit" disabled={saving} className="pb-btn-primary flex-1" data-testid="nu-save">{saving ? "Opslaan…" : (editing ? "Opslaan" : "Aanmaken")}</button>
        </div>
      </form>
    </div>
  );
}

function TeamsModal({ onClose, teams, users, onChanged }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/teams", { name: name.trim() });
      setName("");
      toast.success("Team aangemaakt");
      await onChanged();
    } catch { toast.error("Fout"); }
    finally { setSaving(false); }
  };

  const removeTeam = async (id) => {
    if (!window.confirm("Team verwijderen? Medewerkers verliezen hun teamkoppeling.")) return;
    try {
      await api.delete(`/teams/${id}`);
      toast.success("Verwijderd");
      await onChanged();
    } catch { toast.error("Kon niet verwijderen"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50">
      <div className="pb-card w-full max-w-lg" data-testid="teams-modal">
        <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
          <div className="pb-heading text-lg font-black flex items-center gap-2"><Users2 size={20}/> Teams beheren</div>
          <button onClick={onClose} className="text-white"><X size={20}/></button>
        </div>
        <div className="p-4">
          <form onSubmit={create} className="flex gap-2 mb-4">
            <input className="pb-input" placeholder="Naam van team (bijv. Ploeg Zuid)" value={name} onChange={(e) => setName(e.target.value)} data-testid="team-name"/>
            <button type="submit" disabled={saving} className="pb-btn-primary whitespace-nowrap" data-testid="team-add">Aanmaken</button>
          </form>
          <div className="text-xs text-slate-600 font-semibold mb-3">
            Wanneer je een project op een teamlid sleept, worden alle teamgenoten automatisch mee-ingepland.
          </div>
          <div className="space-y-2">
            {teams.length === 0 && <div className="text-sm text-slate-500 font-medium">Nog geen teams.</div>}
            {teams.map((t) => {
              const members = users.filter((u) => u.team_id === t.id);
              return (
                <div key={t.id} className="border-2 border-slate-900 rounded p-3" data-testid={`team-row-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="pb-heading font-black">{t.name}</div>
                      <div className="text-xs text-slate-600 font-semibold">{members.length} lid{members.length !== 1 ? "en" : ""}</div>
                    </div>
                    <button className="text-red-600 p-2" onClick={() => removeTeam(t.id)} data-testid={`team-delete-${t.id}`}><Trash2 size={18}/></button>
                  </div>
                  {members.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {members.map((m) => (
                        <span key={m.id} className="text-xs font-bold bg-slate-100 border border-slate-900 rounded px-2 py-1">{m.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-4 border-t-2 border-slate-900">
          <button onClick={onClose} className="pb-btn-outline w-full">Sluiten</button>
        </div>
      </div>
    </div>
  );
}
